import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { useLocation } from 'react-router-dom';
import { DotLottieReact } from '@lottiefiles/dotlottie-react';
import { notify } from '../utils/notify';
import '../styles/atualizacoes.css';

// Animação do aviso. Tem de ser a URL DIRETA do arquivo (.json ou .lottie): a de
// /embed/ devolve a página HTML do player do lottie.host e não carrega aqui.
// Esta é 512x512, quadrada, vetorial e ~4 KB, com 4 s em laço a 60 fps.
const ANIMACAO = 'https://lottie.host/699590d7-b3a4-4bc0-b9e0-6915e424042d/SgAYQxM4Sc.json';

/**
 * Por que este renderConfig existe (nao remover):
 *
 * A animacao e vetorial pura (512x512, sem nenhuma imagem embutida), entao ela
 * PODE ficar nitida em qualquer tamanho. Quem borra e o canvas.
 *
 * O dotlottie-web dimensiona o canvas em _syncCanvasSize() como
 * "getBoundingClientRect() x devicePixelRatio", mas so liga o ResizeObserver quando
 * renderConfig.autoResize e verdadeiro. Sem renderConfig o canvas e medido UMA vez,
 * quando a animacao termina de carregar, e nunca mais. Qualquer mudanca depois disso
 * (zoom do navegador, redimensionar a janela, o modal assentando o layout) deixa o
 * bitmap antigo esticado pelo CSS: e esse o borrao. O app ainda aplica
 * "html { zoom: 0.75 }" em index.css, o que reforca a necessidade do piso de 2.
 *
 * autoResize religa a medicao; o piso de 2 no devicePixelRatio mantem o traco nitido
 * em tela sem HiDPI e com zoom do navegador. Custo: ~2 MB de memoria de canvas.
 */
const RENDER_CONFIG = {
  autoResize: true,
  devicePixelRatio: Math.max(typeof window !== 'undefined' ? window.devicePixelRatio || 1 : 1, 2),
};

// De quanto em quanto tempo procurar aviso novo SEM o usuario dar F5. O modal so
// buscava as pendencias ao montar, entao atualizacao publicada durante o dia so
// aparecia no proximo recarregamento da pagina.
const INTERVALO_VERIFICACAO_MS = 10 * 60 * 1000; // 10 minutos

// Rotas onde o aviso nunca aparece (públicas ou full-bleed).
const ROTAS_SEM_AVISO = ['/login', '/logout', '/acesso-negado', '/fmp', '/admissao', '/desligamento', '/termos-de-uso'];

const formatarData = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString('pt-BR');
};

// Acessibilidade (layout.md secao 11): quem pede menos movimento nao recebe a animacao rodando.
const prefereMenosMovimento = () =>
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/**
 * Modal bloqueante com as atualizações do sistema que o operador ainda não confirmou.
 * Montado uma vez no LayoutWrapper; busca as pendentes ao montar, repete a consulta
 * de tempos em tempos e some após o OK.
 * Falha silenciosa no GET: um aviso nunca pode derrubar a tela.
 */
const AtualizacoesModal = () => {
  const location = useLocation();
  const [pendentes, setPendentes] = useState([]);
  const [enviando, setEnviando] = useState(false);
  // Aviso encontrado DEPOIS que a pessoa já estava no sistema: a tela dela veio
  // do build anterior e só troca com um carregamento novo (sair e entrar).
  const [exigeRelogin, setExigeRelogin] = useState(false);
  // false até a primeira consulta bem-sucedida DESTE carregamento da página.
  // Como o componente monta uma vez por carregamento, "primeira consulta" é o
  // mesmo que "acabou de entrar ou de dar F5" — aí o front já é o novo.
  const jaConsultou = useRef(false);

  const podeMostrar =
    !ROTAS_SEM_AVISO.includes(location.pathname) &&
    !!localStorage.getItem('access') &&
    localStorage.getItem('isCliente') !== 'true';

  const carregar = useCallback(async () => {
    try {
      const resp = await axios.get(`${process.env.REACT_APP_API_BASE}/api/atualizacoes/pendentes/`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('access')}` },
      });
      const lista = resp.data?.atualizacoes || [];
      setPendentes(lista);
      if (lista.length > 0 && jaConsultou.current) setExigeRelogin(true);
      jaConsultou.current = true;   // só marca no sucesso: GET que falhou não conta como acesso
    } catch (e) {
      setPendentes([]);
    }
  }, []);

  useEffect(() => {
    if (podeMostrar) carregar();
  }, [podeMostrar, carregar]);

  const aberto = podeMostrar && pendentes.length > 0;

  // Verificacao periodica: enquanto a pessoa esta no app, procura aviso novo de tempos
  // em tempos e assim que ela volta para a aba. Nao consulta com o modal ja aberto
  // (nao ha o que somar) nem com a aba em segundo plano (economiza requisicao).
  useEffect(() => {
    if (!podeMostrar || aberto) return undefined;

    const verificarSeVisivel = () => {
      if (document.visibilityState === 'visible') carregar();
    };

    const timer = setInterval(verificarSeVisivel, INTERVALO_VERIFICACAO_MS);
    document.addEventListener('visibilitychange', verificarSeVisivel);

    return () => {
      clearInterval(timer);
      document.removeEventListener('visibilitychange', verificarSeVisivel);
    };
  }, [podeMostrar, aberto, carregar]);

  useEffect(() => {
    if (aberto) document.body.classList.add('modal-open');
    else document.body.classList.remove('modal-open');
    return () => document.body.classList.remove('modal-open');
  }, [aberto]);

  const confirmar = async () => {
    setEnviando(true);
    try {
      await axios.post(
        `${process.env.REACT_APP_API_BASE}/api/atualizacoes/confirmar/`,
        { ids: pendentes.map((p) => p.id) },
        { headers: { Authorization: `Bearer ${localStorage.getItem('access')}` } }
      );
      setPendentes([]);
    } catch (e) {
      notify.erro('Não foi possível registrar a leitura. Tente novamente.');
    } finally {
      setEnviando(false);
    }
  };

  if (!aberto) return null;

  return (
    <>
      <div className="atu-overlay"></div>

      <div className="atu-palco" role="dialog" aria-modal="true" aria-labelledby="atu-titulo">
        <div className="atu-card">
          <div className="atu-header">
            <div className="atu-header-topo">
              <i className="fas fa-bullhorn atu-icone" aria-hidden="true"></i>
              <h5 className="atu-titulo" id="atu-titulo">Atualizações do sistema</h5>
            </div>
            <small className="atu-subtitulo">Novidades desde o seu último acesso</small>
          </div>

          <div className="atu-body">
            <div className="atu-anim">
              <div className="atu-anim-caixa">
                <DotLottieReact
                  src={ANIMACAO}
                  loop
                  autoplay={!prefereMenosMovimento()}
                  renderConfig={RENDER_CONFIG}
                />
              </div>
            </div>

            {pendentes.map((p) => (
              <div className="atu-item" key={p.id}>
                <div className="atu-meta">
                  <span className="atu-data">{formatarData(p.publicado_em)}</span>
                  {p.versao && <span className="atu-versao">v{p.versao}</span>}
                </div>
                <h6 className="atu-item-titulo">{p.titulo}</h6>
                <ul className="atu-lista">
                  {String(p.descricao || '').split('\n').filter((l) => l.trim()).map((linha, i) => (
                    <li key={i}>{linha}</li>
                  ))}
                </ul>
              </div>
            ))}

            {/* Só quando o aviso chegou com a pessoa já trabalhando: o navegador
                dela ainda está com a versão anterior do sistema. */}
            {exigeRelogin && (
              <div className="atu-aviso-sessao" role="alert">
                <i className="fas fa-right-from-bracket" aria-hidden="true"></i>
                <div>
                  <strong>Saia e entre de novo para receber esta atualização.</strong>
                  <span>
                    Você já estava no sistema quando ela foi publicada, então esta tela ainda é a
                    versão anterior. Termine o que está fazendo, faça logout e entre outra vez.
                  </span>
                </div>
              </div>
            )}
          </div>

          <div className="atu-footer">
            <button type="button" className="atu-btn" onClick={confirmar} disabled={enviando}>
              {enviando ? (
                <>
                  <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                  Registrando...
                </>
              ) : (
                <>
                  {exigeRelogin ? 'OK, vou sair e entrar' : 'OK, li as atualizações'}
                  <span aria-hidden="true">&rarr;</span>
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default AtualizacoesModal;
